import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import type { TrackerTag } from '@racemap/sdk/schema/trackerTags';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { createTrackerShareLink } from '@racemap/utilities/functions/createTrackerShareLink';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { useStore } from '../../../store/reducers';
import { HStack } from '../../BasicComponents/MetaComponent';
import { IconTag } from '../../Icon';
import { type TrackerDropItem, TrackerDropZone } from '../TrackerDropZone';
import { TagModal } from './TagModal';

interface Props {
  tag: Immutable<TrackerTag>;
}
export const TrackerTagEditField: FC<Props> = ({ tag }) => {
  const { updateTrackerTag, removeTrackerTag } = useStore((s) => s.trackers.actions);

  const handleTagUpdate = async (key: keyof TrackerTag, newValue: string | Array<string>) => {
    await updateTrackerTag(tag.id, { [key]: newValue });
  };

  const handleTrackerAdd = async (trackerIds: Array<string>) => {
    const trackerIdsCombined = Array.from(
      new Set([...tag.trackerIds.map((id) => id.toHexString()), ...trackerIds]),
    );
    await updateTrackerTag(tag.id, {
      trackerIds: trackerIdsCombined.map((tId) => new ObjectId(tId)),
    });
  };

  const handleTrackersRemove = async (trackerIds: Array<string>) => {
    await updateTrackerTag(tag.id, {
      trackerIds: tag.trackerIds.filter((id) => !trackerIds.includes(id.toHexString())),
    });
  };

  const handleTagColorChange = async (color: string) => {
    await updateTrackerTag(tag.id, { color });
  };

  return (
    <TrackerDropZone
      acceptedElementIds={['sample-trackers', 'total-trackers']}
      withIsOver={true}
      onDrop={(item) => handleTrackerAdd(Array.from((item as TrackerDropItem).data.ids))}
    >
      <Container key={tag.id.toHexString()}>
        <TagAndLabel>
          <IconTag color={tag.color} />
          <div>{tag.label || ''}</div>
          <TagModal
            tag={tag}
            handleTrackersRemove={handleTrackersRemove}
            handleTagUpdate={handleTagUpdate}
            handleTagColorChange={handleTagColorChange}
            removeTrackerTag={removeTrackerTag}
            shareLink={createTrackerShareLink(tag.trackerIds.map((id) => id.toHexString()))}
          />
        </TagAndLabel>
      </Container>
    </TrackerDropZone>
  );
};

const Container = styled(HStack)`
  .form-group {
    margin-bottom: 0;
  }

  input {
    padding: 0.375rem 2px;
    height: 1.5rem;
    margin-left: 2px;
  }

  :hover {
    background: ${RacemapColors.CloudBlue};
    border-radius: 1px;
  }
`;
const TagAndLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
